import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Music, FileText, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';

const isDirectVideoFile = (url) => {
  const exts = ['.mp4', '.webm', '.ogg', '.m3u8', '.mpd'];
  return exts.some(ext => url.toLowerCase().includes(ext));
};

/**
 * Converts a video URL to an embeddable URL.
 * Returns null if it should be rendered as a native <video> element.
 */
const getVideoEmbed = (url) => {
  // YouTube
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}?autoplay=1`;

  // Vimeo
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}?autoplay=1`;

  // Generic streaming page (not a direct video file) → embed via iframe
  if (!isDirectVideoFile(url)) return url;

  return null; // direct video file → use <video> tag
};

const isPdfUrl = (url) => /\.pdf(\?|$)/i.test(url);

const ResourcePreviewModal = ({ resource, onClose }) => {
  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  if (!resource) return null;
  const { content_type, content_url, name } = resource;
  const filename = content_url.split('/').pop();

  const modal = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/85 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative z-10 w-full max-w-4xl mx-4 flex flex-col max-h-[92vh]">
        {/* Header bar */}
        <div className="flex items-center justify-between px-4 py-2 bg-black/60 rounded-t-lg">
          <span className="text-white/90 text-sm font-medium truncate max-w-[80%]">{name}</span>
          <div className="flex items-center gap-2">
            <a
              href={content_url}
              target="_blank"
              rel="noreferrer"
              className="text-white/60 hover:text-white transition-colors"
              title="Abrir en nueva pestaña"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
            <button
              onClick={onClose}
              className="text-white/60 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto rounded-b-lg bg-black/40 flex items-center justify-center">
          {content_type === 'image' && (
            <img
              src={content_url}
              alt={name}
              className="max-w-full max-h-[80vh] object-contain rounded-b-lg"
              onClick={(e) => e.stopPropagation()}
            />
          )}

          {content_type === 'video' && (() => {
            const embedUrl = getVideoEmbed(content_url);
            return embedUrl ? (
              <div className="w-full" style={{ paddingBottom: '56.25%', position: 'relative' }}>
                <iframe
                  src={embedUrl}
                  title={name}
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full rounded-b-lg"
                />
              </div>
            ) : (
              <video
                src={content_url}
                controls
                autoPlay
                className="max-w-full max-h-[80vh] rounded-b-lg"
              />
            );
          })()}

          {content_type === 'audio' && (
            <div className="flex flex-col items-center gap-6 p-10">
              <div className="w-24 h-24 rounded-full bg-primary/20 flex items-center justify-center">
                <Music className="w-12 h-12 text-primary" />
              </div>
              <p className="text-white/80 text-sm text-center max-w-xs break-all">{filename}</p>
              <audio
                src={content_url}
                controls
                autoPlay
                className="w-full max-w-md"
              />
            </div>
          )}

          {content_type === 'document' && (() => {
            const canEmbed = isPdfUrl(content_url);
            return canEmbed ? (
              <iframe
                src={content_url}
                title={name}
                className="w-full rounded-b-lg bg-white"
                style={{ height: '80vh' }}
              />
            ) : (
              // PPT / DOCX — try Google Docs viewer (works on public URLs)
              <iframe
                src={`https://docs.google.com/viewer?url=${encodeURIComponent(content_url)}&embedded=true`}
                title={name}
                className="w-full rounded-b-lg bg-white"
                style={{ height: '80vh' }}
              />
            );
          })()}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default ResourcePreviewModal;
